import React, { useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FormProvider, useForm } from "react-hook-form";
import { NavLink, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetFartherManagedAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useGetFartherManagedAccounts";
import useFundingAccountExtended from "@fartherfinance/frontend/api/ExternalAccount/hooks/useFundingAccountExtended";
import useAllAccountBalancesV4 from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalancesV4";
import useCreateAcatsTransfer, {
  Input as ACATSTransfer,
} from "@fartherfinance/frontend/api/Transfer/hooks/useCreateAcatsTransfer";
import useCreateACHDeposit, {
  Deposit,
  IraDeposit,
} from "@fartherfinance/frontend/api/Transfer/hooks/useCreateACHDeposit";
import useCreateACHWithdrawalV1, {
  WithdrawalV1,
} from "@fartherfinance/frontend/api/Transfer/hooks/useCreateACHWithdrawalV1";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { TransfersV2Path } from "@src/config/routing/RouterPaths";
import BaseLayout from "@src/multiCustodian/components/Layouts/BaseLayout/BaseLayout";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import LogoWordmark from "@src/multiCustodian/theme/LogoWordmark";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import Text from "@src/sharedComponents/Typography/Text";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";

import {
  CreateTransferDepositFormData,
  CreateTransferFormData,
  CreateTransferInvestmentFormData,
  CreateTransferIRAFormData,
  CreateTransferWithdrawalFormData,
} from "./const";
import CreateTransferForm from "./CreateTransferForm";
import CreateTransferSummary from "./CreateTransferSummary";
import ErrorStateModal from "./ErrorStateModal";
import {
  isAccountTypeIRA,
  isDepositForm,
  isInvestmentForm,
  isIRAForm,
  isWithdrawalForm,
  prepareTransferSummary,
} from "./utils";

import styles from "./CreateTransfer.module.css";

enum CreateTransferStep {
  TransferForm = 1,
  TransferSummary,
}

const CreateTransfer: React.FC = () => {
  const { isImpersonationMode } = useApplicationMode();
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();
  const [transferStep, setTransferStep] = useState<CreateTransferStep>(
    CreateTransferStep.TransferForm
  );

  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const navigate = useNavigate();
  const form = useForm<CreateTransferFormData>({
    mode: "onChange",
  });

  const auth = useRequestAuth();
  const statusNotification = useStatusNotification();

  const [statementFile, setStatementFile] = useState<File | null>(null);

  const fundingAccountQuery = useFundingAccountExtended(auth);
  const managedAccountsQuery = useGetFartherManagedAccounts(clientId, auth);
  const allAccountBalancesQuery = useAllAccountBalancesV4(clientId, true, auth);

  const callCreateDeposit = useCreateACHDeposit(auth);
  const callCreateWithdrawalV1 = useCreateACHWithdrawalV1(auth);
  const callCreateAcatsTransfer = useCreateAcatsTransfer(auth);

  const fundingAccount = fundingAccountQuery.data;
  const managedAccounts = managedAccountsQuery.data ?? [];
  const allAccountBalances = allAccountBalancesQuery.data ?? [];

  const handleMoveToPrevScreen = (transferStep: number) => {
    if (transferStep === CreateTransferStep.TransferForm) {
      navigate(`/Client/${clientId}/${TransfersV2Path}`);
    } else if (transferStep === CreateTransferStep.TransferSummary) {
      setTransferStep(CreateTransferStep.TransferForm);
    }
  };

  const handleMoveToTransferScreen = (): void => {
    setTransferStep(CreateTransferStep.TransferForm);
  };

  const handleMoveToSummaryScreen = (): void => {
    setTransferStep(CreateTransferStep.TransferSummary);
  };

  const handleOnContinuteClick = (): void => {
    form.trigger().then((isValid) => isValid && handleMoveToSummaryScreen());
  };

  const handleSubmitDepositForm = async (
    formData: CreateTransferDepositFormData
  ): Promise<unknown> => {
    const { depositAmount, depositAccount, depositContributionYear } = formData;
    const { account } = depositAccount;

    const { virtualAccountId } = account;
    const { accountType } = account.accountDetails;

    const depositBody: Deposit | IraDeposit = {
      fartherAccountId: virtualAccountId,
      amount: parseFloat(removeCommas(depositAmount)),
      ...(isAccountTypeIRA(accountType) && {
        // See https://linear.app/fartherfinance/issue/BE-520/bug-with-contribution-to-sep-iras#comment-8069b8a3
        // when to choose which options are in doc below
        // https://developer.apexclearing.com/apexclearing/docs/ira-details#ira-contribution-types
        contributionType: accountType === "Sep IRA" ? "EMPLOYER" : "REGULAR",
        contributionYear: parseInt(depositContributionYear.label),
      }),
    };

    return callCreateDeposit(depositBody);
  };

  const handleSubmitWithdrawalForm = async (
    formData: CreateTransferWithdrawalFormData
  ): Promise<unknown> => {
    const { withdrawalAmount, withdrawalAccount } = formData;
    const { account } = withdrawalAccount;

    const { virtualAccountId } = account;

    const withdrawalBody: WithdrawalV1 = {
      fartherAccountId: virtualAccountId,
      // NOTE: we no longer send a "null" value for full withdrawal, we rely on proper cash in account value
      amount: parseFloat(removeCommas(withdrawalAmount)),
      bypassLiquidation: true,
    };

    return callCreateWithdrawalV1(withdrawalBody);
  };

  const handleSubmitInvestmentForm = async (
    formData: CreateTransferInvestmentFormData
  ): Promise<unknown> => {
    const {
      accountType,
      institution,
      accountNumber,
      accountHolderName,
      accountDestination,
    } = formData;

    const acatsTransferBody: ACATSTransfer = {
      acatsTransferDataInfo: {
        type: accountType.accountType,
        institutionId: institution.institutionId,
        accountNumber: accountNumber,
        holderName: accountHolderName,
        accountId: accountDestination.accountId,
      },
      statement: statementFile || undefined,
    };

    return callCreateAcatsTransfer(acatsTransferBody);
  };

  const handleSubmitIRAForm = async (
    formData: CreateTransferIRAFormData
  ): Promise<unknown> => {
    const {
      iraType,
      institution,
      accountNumber,
      accountHolderName,
      accountDestination,
    } = formData;

    const iraTransferBody: ACATSTransfer = {
      acatsTransferDataInfo: {
        type: iraType.iraType,
        institutionId: institution.institutionId,
        accountNumber: accountNumber,
        holderName: accountHolderName,
        accountId: accountDestination.accountId,
      },
      statement: statementFile || undefined,
    };

    return callCreateAcatsTransfer(iraTransferBody);
  };

  const handleOnSubmitClick = (): void => {
    form.handleSubmit(async (formData) => {
      try {
        if (isDepositForm(formData)) {
          await handleSubmitDepositForm(formData);
        }

        if (isWithdrawalForm(formData)) {
          await handleSubmitWithdrawalForm(formData);
        }

        if (isInvestmentForm(formData)) {
          await handleSubmitInvestmentForm(formData);
        }

        if (isIRAForm(formData)) {
          await handleSubmitIRAForm(formData);
        }

        navigate(`/Client/${clientId}/${TransfersV2Path}`);

        statusNotification(
          "Your transfer is on its way! It should process in 3-5 business days",
          "Success"
        );
      } catch (_error) {
        setIsErrorModalVisible(true);
      }
    })();
  };

  return (
    <BaseLayout showSideNav={false}>
      <PageHero
        className={styles.topBar}
        start={
          <div>
            <NavLink to={`/Client/${clientId}/Dashboard`}>
              <LogoWordmark className={styles.fartherLogo} />
            </NavLink>
          </div>
        }
      />

      <PageHero
        className={styles.hero}
        start={
          <div className={styles.heroStart}>
            <Text
              className={styles.arrowLink}
              onClick={() => handleMoveToPrevScreen(transferStep)}
            >
              <ArrowBackIcon className={styles.arrowIcon} />
            </Text>

            <Text variant="title1">Make a Transfer</Text>
          </div>
        }
        end={
          <div className={styles.heroEnd}>
            <LinkButton
              text="Cancel"
              variant="text"
              buttonType="primary"
              to={`/Client/${clientId}/${TransfersV2Path}`}
            />

            {transferStep === CreateTransferStep.TransferForm && (
              <Button
                sx={{ width: 200 }}
                variant="contained"
                size="medium"
                buttonType="primary"
                text="Continue"
                disabled={!form.formState.isValid}
                onClick={handleOnContinuteClick}
              />
            )}

            {transferStep === CreateTransferStep.TransferSummary && (
              <Button
                sx={{ width: 200 }}
                variant="contained"
                size="medium"
                buttonType="primary"
                text="Submit"
                onClick={handleOnSubmitClick}
              />
            )}
          </div>
        }
      />

      {transferStep === CreateTransferStep.TransferForm && (
        <FormProvider {...form}>
          <CreateTransferForm
            fundingAccount={fundingAccount}
            managedAccounts={managedAccounts}
            allAccountBalances={allAccountBalances}
            setStatementFile={setStatementFile}
            statementFile={statementFile}
          />
        </FormProvider>
      )}

      {transferStep === CreateTransferStep.TransferSummary && (
        <CreateTransferSummary
          onEditClick={handleMoveToTransferScreen}
          {...prepareTransferSummary(
            form.getValues(),
            allAccountBalances,
            isImpersonationMode
          )}
          statementFile={statementFile}
        />
      )}

      {isErrorModalVisible && (
        <ErrorStateModal closeModal={() => setIsErrorModalVisible(false)} />
      )}
    </BaseLayout>
  );
};

export default CreateTransfer;
